import type { DeviceSummary, NewMatchEvent, ScoringMode } from '@aiscoreboard/contracts';
import { listDevices, updateDevice } from './accounts.ts';
import { isCameraAvailable, isCameraLicenseExpired } from './camera.ts';
import { PREFERRED_DEVICE_KEY, preferredPairing } from './devicePairing.ts';
import { controlToken } from './operator.ts';
import { getPairedYouTubeDevice, youtubeDevicePath, youtubeRequest } from './youtube.ts';
import type { MessageKey } from './i18n';

export class CameraControlError extends Error {
  readonly key: MessageKey;
  constructor(key: MessageKey) { super(key); this.key = key; }
}

export interface CameraControlTarget {
  device: DeviceSummary;
  token: string;
  paired: boolean;
}

export async function cameraControlTarget(matchId?: string, allowPreferred = false): Promise<CameraControlTarget> {
  const pairing = preferredPairing();
  if (pairing) {
    const result = await getPairedYouTubeDevice(pairing.token, pairing.deviceId);
    if (!matchId || result.device.matchId === matchId || allowPreferred) {
      return { device: result.device, token: pairing.token, paired: true };
    }
  }
  const token = controlToken();
  const preferredId = localStorage.getItem(PREFERRED_DEVICE_KEY);
  if (token) {
    const owned = (await listDevices(token)).devices;
    const device = owned.find(item => matchId ? item.matchId === matchId : item.id === preferredId)
      ?? (allowPreferred ? owned.find(item => item.id === preferredId) : undefined);
    if (device) return { device, token, paired: false };
  }
  throw new CameraControlError('youtube.loginRequired');
}

export function assertCameraFeature(device: DeviceSummary, ai: boolean): void {
  if (isCameraLicenseExpired(device)) throw new CameraControlError('match.licenseExpired');
  if (ai && device.aiSupported === false) throw new CameraControlError('match.aiUnsupported');
  if (!isCameraAvailable(device)) throw new CameraControlError('match.cameraNotFound');
  if (ai && device.license?.enforcementEnabled && device.license.plan !== 'ai_streaming') {
    throw new CameraControlError('match.aiUnsupported');
  }
}

export async function bindCameraControl(target: CameraControlTarget, matchId: string): Promise<void> {
  if (target.device.matchId === matchId) return;
  if (target.paired) {
    await youtubeRequest(target.token, `${youtubeDevicePath(target.device.id)}/paired`, 'PATCH', { matchId });
  } else if (target.token) {
    await updateDevice(target.token, target.device.id, { matchId });
  } else {
    throw new CameraControlError('youtube.loginRequired');
  }
}

export async function changeCameraStreaming(
  target: CameraControlTarget, matchId: string, enabled: boolean,
  dispatch: (event: NewMatchEvent) => Promise<boolean>,
): Promise<void> {
  if (enabled) assertCameraFeature(target.device, false);
  if (target.device.youtubeManaged) {
    if (!target.token) throw new CameraControlError('youtube.loginRequired');
    await youtubeRequest(target.token, `${youtubeDevicePath(target.device.id)}/${enabled ? 'start' : 'stop'}`,
      'POST', enabled ? { matchId } : {});
  } else {
    if (enabled && (!target.device.rtmpUrl || !target.device.hasStreamKey)) {
      throw new CameraControlError('match.cameraStreamingNotReady');
    }
    if (enabled) await bindCameraControl(target, matchId);
    if (!await dispatch({ type: 'streaming.changed', source: 'manual', enabled })) {
      throw new Error('Streaming change was not confirmed');
    }
  }
}

export async function changeCameraMode(
  target: CameraControlTarget, matchId: string, mode: ScoringMode,
  dispatch: (event: NewMatchEvent) => Promise<boolean>,
): Promise<void> {
  if (mode !== 'manual') {
    assertCameraFeature(target.device, true);
    await bindCameraControl(target, matchId);
  }
  if (!await dispatch({ type: 'mode.changed', source: 'manual', mode })) {
    throw new Error('AI mode change was not confirmed');
  }
}
